// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { AddRounded, SearchRounded } from '@/constants/hardfin-standard-icons';
import { SparseItem } from '@/sparse/api/types';
import {
  Autocomplete,
  AutocompleteFreeSoloValueMapping,
  AutocompleteProps,
  AutocompleteRenderInputParams,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ForwardedRef, HTMLAttributes, forwardRef } from 'react';

/**
 * Special ID used to identify the "create new item" option in the dropdown.
 */
export const CREATE_ITEM_OPTION_ID = 'create-item';

function isOptionEqualToValue(option: SparseItem, value: SparseItem) {
  return option.id === value.id;
}

function renderOption(
  props: HTMLAttributes<HTMLLIElement>,
  option: SparseItem
) {
  if (option.id === CREATE_ITEM_OPTION_ID) {
    return (
      <li {...props} key={option.id}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <AddRounded fontSize="small" color="primary" />
          <Typography color="primary.main">
            Add item <b>{option.name}</b>
          </Typography>
        </Stack>
      </li>
    );
  }

  return (
    <li {...props} key={option.id}>
      <Typography>
        {option.sku}
        <Typography component="span" color="text.secondary">
          {' '}
          ({option.name})
        </Typography>
      </Typography>
    </li>
  );
}

function getOptionLabel<FreeSolo extends boolean | undefined>(
  option: SparseItem | AutocompleteFreeSoloValueMapping<FreeSolo>
) {
  if (typeof option === 'string') {
    return option;
  }

  return `${option.sku} (${option.name})`;
}

interface Props<Option extends SparseItem, FreeSolo extends boolean | undefined>
  extends Omit<
    AutocompleteProps<Option, false, false, FreeSolo>,
    'renderInput' | 'isOptionEqualToValue' | 'getOptionLabel' | 'renderOption'
  > {
  name?: string;
  label?: string;
  error?: boolean;
  helperText?: string;
  placeholder?: string;
  showSearchAdornment?: boolean;
}

function BulkItemAutocompleteRenderer<
  Option extends SparseItem,
  FreeSolo extends boolean | undefined,
>(props: Props<Option, FreeSolo>, ref: ForwardedRef<HTMLInputElement>) {
  const {
    name,
    label,
    placeholder,
    error,
    helperText,
    showSearchAdornment,
    ...autocompleteProps
  } = props;

  const renderInput = (parameters: AutocompleteRenderInputParams) => {
    const { InputProps, inputProps, ...remainingProps } = parameters;
    return (
      <TextField
        inputRef={ref}
        name={name}
        label={label}
        error={error}
        helperText={helperText}
        placeholder={placeholder}
        {...remainingProps}
        InputProps={{
          ...InputProps,
          startAdornment: (
            <>
              {showSearchAdornment && (
                <InputAdornment position="start">
                  <SearchRounded />
                </InputAdornment>
              )}
              {InputProps?.startAdornment}
            </>
          ),
        }}
        inputProps={inputProps}
      />
    );
  };

  return (
    <Autocomplete
      {...autocompleteProps}
      renderInput={renderInput}
      renderOption={renderOption}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      noOptionsText="No items available"
    />
  );
}

export const BulkItemAutocomplete = forwardRef(BulkItemAutocompleteRenderer);
